// ✅ ALTERED ONLY FOR THEME (matches the “above theme”)
// - Orange header bar + white title
// - Page bg #F2F2F2 + inner white surfaces with soft blue border
// - Inputs: soft blue border + orange focus ring
// - Buttons: primary orange, outlined blue
// - Table: blue header, soft zebra rows + hover tint
// - IconButtons: blue default, orange on hover
// - No business/API logic changed

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import {
  Box,
  TextField,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  Typography,
  Button,
  FormControl,
  InputLabel,
  CircularProgress,
  Autocomplete,
  InputAdornment,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import UploadIcon from "@mui/icons-material/Upload";
import SearchIcon from "@mui/icons-material/Search";
import { API_URL } from "./config";

const AssetManagement = () => {
  const DEFAULT_CAMPUS_CODE = "KIET";

  // ✅ Theme constants
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const THEME_BLUE = "#1976d2";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";

  const pageWrapSx = {
    p: 2,
    backgroundColor: BG,
    minHeight: "100vh",
  };

  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };

  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };

  const bodySx = {
    p: 2,
    backgroundColor: BG,
  };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#fff",
      "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
    },
    "& .MuiInputLabel-root": { color: "text.secondary" },
    "& .MuiInputLabel-root.Mui-focused": { color: THEME_ORANGE },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };

  const iconBtnSx = {
    borderRadius: 2,
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      backgroundColor: "rgba(240,120,40,0.10)",
      color: THEME_ORANGE,
    },
  };

  const tableWrapSx = {
    ...surfaceSx,
    mt: 1,
    borderRadius: 2,
    overflow: "hidden",
  };

  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
      fontSize: "0.78rem",
    },
  };

  const zebraRowSx = (idx) => ({
    backgroundColor: idx % 2 === 0 ? ROW_ALT : "#FFFFFF",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": { borderBottom: "1px solid rgba(25,118,210,0.10)" },
  });

  const badgeSx = (status) => {
    const colors = {
      Active: { bg: "#DCFCE7", text: "#166534", border: "#BBF7D0" },
      Maintenance: { bg: "#FEF3C7", text: "#92400E", border: "#FDE68A" },
      Damaged: { bg: "#FEE2E2", text: "#991B1B", border: "#FECACA" },
      Inactive: { bg: "#F3F4F6", text: "#374151", border: "#E5E7EB" },
      "In Progress": { bg: "#DBEAFE", text: "#1E40AF", border: "#BFDBFE" },
      Completed: { bg: "#DCFCE7", text: "#166534", border: "#BBF7D0" },
      Required: { bg: "#FEE2E2", text: "#991B1B", border: "#FECACA" },
      "Not Required": { bg: "#F3F4F6", text: "#374151", border: "#E5E7EB" },
    };

    const c = colors[status] || colors["Not Required"];

    return {
      px: 1.5,
      py: 0.5,
      borderRadius: "999px",
      display: "inline-flex",
      alignItems: "center",
      fontWeight: 700,
      fontSize: "0.70rem",
      backgroundColor: c.bg,
      color: c.text,
      border: `1px solid ${c.border}`,
      textTransform: "uppercase",
      whiteSpace: "nowrap",
    };
  };

  // ─── State ──────────────────────────────────────────────────────────────────
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [subCampusFilter, setSubCampusFilter] = useState("All");
  const [typeFilter, setTypeFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [campusCode, setCampusCode] = useState(DEFAULT_CAMPUS_CODE);

  const [newAsset, setNewAsset] = useState({
    AssetID: "",
    CampusCode: DEFAULT_CAMPUS_CODE,
    SubCampusCode: "",
    AssetCategory: "",
    AssetType: "",
    AssetName: "",
    SerialNumber: "",
    RoomType: "",
    RoomNumber: "",
    Status: "Active",
    Maintenance: "Not Required",
    Repair: "Not Required",
  });

  const [masterSubCampuses, setMasterSubCampuses] = useState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTarget, setDialogTarget] = useState("");
  const [editingAssetId, setEditingAssetId] = useState(null);
  const [dialogValue, setDialogValue] = useState("");

  const [manualCategories, setManualCategories] = useState([]);
  const [manualTypes, setManualTypes] = useState([]);
  const [manualSubCampuses, setManualSubCampuses] = useState([]);

  const formatDate = (dateStr) => {
    if (!dateStr || dateStr === "-") return "-";
    return dateStr.split("T")[0];
  };

  const token = localStorage.getItem("auth");

  const getAuthHeaders = useCallback(() => {
    const authToken = localStorage.getItem("auth");
    return authToken ? { Authorization: `Bearer ${authToken}` } : {};
  }, []);

  const isAdmin = useMemo(() => {
    const tokenStr = localStorage.getItem("auth");
    if (!tokenStr) return false;
    try {
      const payload = JSON.parse(
        atob(tokenStr.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))
      );
      const roles = payload?.roles || (payload?.role ? [payload.role] : []);
      const rolesArr = Array.isArray(roles) ? roles : [roles];
      return rolesArr.some((r) => String(r).toLowerCase() === "admin");
    } catch {
      return false;
    }
  }, []);

  const fileInputRef = useRef(null);
  const handleBulkClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("excelFile", file);
    formData.append("CampusCode", campusCode);

    try {
      setLoading(true);
      await axios.post(`${API_URL}/api/assets/upload`, formData, {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "multipart/form-data",
        },
      });
      setSuccess(true);
      setError("");
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.error || "Bulk upload failed");
    } finally {
      setLoading(false);
      e.target.value = "";
    }
  };

  const uniqueSubCampusesList = useMemo(() => {
    const listFromAssets = assets.map((a) => a.SubCampusCode).filter(Boolean);
    const listFromMaster = masterSubCampuses.map((s) => s.SubCampusCode).filter(Boolean);
    return [...new Set([...listFromAssets, ...listFromMaster, ...manualSubCampuses])].sort();
  }, [assets, masterSubCampuses, manualSubCampuses]);

  const uniqueSubCampuses = useMemo(() => {
    const defaults = ["K1", "KK", "KW"];
    return [...new Set([...defaults, ...uniqueSubCampusesList])].sort();
  }, [uniqueSubCampusesList]);

  const uniqueCategories = useMemo(() => {
    const defaults = ["Multimedia", "Networking", "Utility", "Workstation"];
    const listFromAssets = assets.map((a) => a.AssetCategory).filter(Boolean);
    return [...new Set([...defaults, ...listFromAssets, ...manualCategories])].sort();
  }, [assets, manualCategories]);

  const uniqueTypesList = useMemo(() => {
    const list = assets.map((a) => a.AssetType).filter(Boolean);
    return [...new Set([...list, ...manualTypes])].sort();
  }, [assets, manualTypes]);

  const uniqueCategoriesWithAll = useMemo(() => {
    return ["All", ...uniqueCategories];
  }, [uniqueCategories]);

  const uniqueSubCampusesWithAll = useMemo(() => {
    return ["All", ...uniqueSubCampuses];
  }, [uniqueSubCampuses]);

  const uniqueTypesWithAll = useMemo(() => {
    return ["All", ...uniqueTypesList];
  }, [uniqueTypesList]);

  const uniqueStatusesWithAll = useMemo(() => {
    const defaults = ["All", "Active", "Maintenance", "Damaged", "Inactive"];
    const fromData = assets.map((a) => a.Status).filter(Boolean);
    return [...new Set([...defaults, ...fromData])].sort((a, b) =>
      a === "All" ? -1 : b === "All" ? 1 : a.localeCompare(b)
    );
  }, [assets]);

  // ─── Fetch Data ─────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const [res, subRes] = await Promise.all([
        axios.get(`${API_URL}/api/assets`, {
          headers: getAuthHeaders(),
        }),
        axios
          .get(`${API_URL}/api/CodeSubCampus`, {
            headers: getAuthHeaders(),
          })
          .catch(() => ({ data: [] })),
      ]);

      if (res.data && Array.isArray(res.data)) {
        setAssets(
          res.data.map((asset) => ({
            ...asset,
            CampusCode: asset.CampusCode || DEFAULT_CAMPUS_CODE,
          }))
        );
      }

      setMasterSubCampuses(Array.isArray(subRes.data) ? subRes.data : []);
    } catch (err) {
      console.error("API failed:", err);
      setError("Failed to fetch data: " + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  }, [DEFAULT_CAMPUS_CODE, getAuthHeaders]);

  useEffect(() => {
    fetchData();
    setCampusCode(DEFAULT_CAMPUS_CODE);
    setNewAsset((prev) => ({ ...prev, CampusCode: DEFAULT_CAMPUS_CODE }));
  }, [fetchData, token]);

  // ─── Handlers ──────────────────────────────────────────────────────────────
  const handleInputChange = (e, id_or_assetId = null) => {
    const { name, value } = e.target;
    if (!id_or_assetId) {
      setNewAsset((prev) => ({ ...prev, [name]: value }));
    } else {
      setAssets((prev) =>
        prev.map((a) =>
          a.id === id_or_assetId || a.AssetID === id_or_assetId ? { ...a, [name]: value } : a
        )
      );
    }
  };

  const handleAdd = async () => {
    if (!newAsset.AssetID || !newAsset.AssetName || !newAsset.SerialNumber) {
      setError("AssetID, Name, and Serial Number are required");
      return;
    }

    const isIdDup = assets.some((a) => a.AssetID === newAsset.AssetID);
    const isSerDup = assets.some((a) => a.SerialNumber === newAsset.SerialNumber);
    if (isIdDup) return setError(`Asset ID "${newAsset.AssetID}" already exists.`);
    if (isSerDup) return setError(`Serial Number "${newAsset.SerialNumber}" already exists.`);

    const today = new Date().toISOString().split("T")[0];
    const assetWithDate = {
      ...newAsset,
      CampusCode: newAsset.CampusCode || DEFAULT_CAMPUS_CODE,
      LastUpdated: today,
    };

    setAssets((prev) => [...prev, assetWithDate]);
    setNewAsset({
      AssetID: "",
      CampusCode: DEFAULT_CAMPUS_CODE,
      SubCampusCode: "",
      AssetCategory: "",
      AssetType: "",
      AssetName: "",
      SerialNumber: "",
      RoomType: "",
      RoomNumber: "",
      Status: "Active",
      Maintenance: "Not Required",
      Repair: "Not Required",
    });
    setSuccess(true);
    setError("");

    try {
      const response = await axios.post(`${API_URL}/api/assets`, assetWithDate, {
        headers: getAuthHeaders(),
      });

      if (response.data.id) {
        setAssets((prev) =>
          prev.map((a) =>
            a.AssetID === assetWithDate.AssetID && !a.id ? { ...a, id: response.data.id } : a
          )
        );
      }

      await fetchData();
    } catch (err) {
      console.warn("DB Sync failed, but added locally:", err);
      setError(err.response?.data?.error || "Database sync failed. Entry is currently local-only.");
    }
  };

  const handleUpdate = async (dbId) => {
    const updated = assets.find((a) => a.id === dbId);
    if (!updated) return;

    const today = new Date().toISOString().split("T")[0];
    const assetWithUpdatedDate = { ...updated, LastUpdated: today };

    const isIdDup = assets.some((a) => a.id !== dbId && a.AssetID === updated.AssetID);
    const isSerDup = assets.some((a) => a.id !== dbId && a.SerialNumber === updated.SerialNumber);

    if (isIdDup) {
      return setError(`Asset ID "${updated.AssetID}" is already used by another asset.`);
    }

    if (isSerDup) {
      return setError(`Serial Number "${updated.SerialNumber}" is already used by another asset.`);
    }

    setAssets((prev) => prev.map((a) => (a.id === dbId ? assetWithUpdatedDate : a)));
    setEditId(null);
    setSuccess(true);

    try {
      await axios.put(`${API_URL}/api/assets/${dbId}`, assetWithUpdatedDate, {
        headers: getAuthHeaders(),
      });
      await fetchData();
    } catch (err) {
      console.warn("DB Sync failed, but updated locally:", err);
      setError(err.response?.data?.error || "Database sync failed. Update is currently local-only.");
    }
  };

  const handleDelete = async (dbId) => {
    if (window.confirm("Delete this asset?")) {
      setAssets((prev) => prev.filter((a) => a.id !== dbId));
      setSuccess(true);

      try {
        await axios.delete(`${API_URL}/api/assets/${dbId}`, {
          headers: getAuthHeaders(),
        });
        await fetchData();
      } catch (err) {
        console.warn("DB Sync failed, but deleted locally:", err);
        setError(err.response?.data?.error || "Database sync failed. Delete is currently local-only.");
      }
    }
  };

  const filteredAssets = assets
    .filter((a) => {
      const matchesSearch = Object.values(a).some((val) =>
        String(val).toLowerCase().includes(search.toLowerCase())
      );
      const matchesCategory = categoryFilter === "All" || a.AssetCategory === categoryFilter;
      const matchesSubCampus = subCampusFilter === "All" || a.SubCampusCode === subCampusFilter;
      const matchesType = typeFilter === "All" || (a.AssetType || a.assettype) === typeFilter;
      const matchesStatus = statusFilter === "All" || a.Status === statusFilter;

      return matchesSearch && matchesCategory && matchesSubCampus && matchesType && matchesStatus;
    })
    .sort((a, b) => (a.id || 0) - (b.id || 0));

  if (loading && assets.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
        <CircularProgress sx={{ color: THEME_ORANGE }} />
      </Box>
    );
  }

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Asset Management
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Add, edit, delete campus assets and manage equipment
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              sx={primaryBtnSx}
              startIcon={<UploadIcon />}
              onClick={handleBulkClick}
            >
              Bulk Upload
            </Button>

            <input
              type="file"
              accept=".xlsx, .xls"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFile}
            />
          </Box>
        </Box>

        <Box sx={bodySx}>
          {!!error && (
            <Alert
              severity="error"
              onClose={() => setError("")}
              sx={{
                borderRadius: 2,
                border: "1px solid rgba(244,67,54,0.25)",
                backgroundColor: "rgba(244,67,54,0.04)",
                mb: 2,
              }}
            >
              {error}
            </Alert>
          )}

          <Box
            sx={{
              ...surfaceSx,
              p: 2,
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
              <FormControl size="small" sx={{ minWidth: 150, ...inputSx }}>
                <InputLabel id="category-filter-label">Category</InputLabel>
                <Select
                  labelId="category-filter-label"
                  value={categoryFilter}
                  label="Category"
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  {uniqueCategoriesWithAll.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 150, ...inputSx }}>
                <InputLabel id="sub-campus-filter-label">Sub-Campus</InputLabel>
                <Select
                  labelId="sub-campus-filter-label"
                  value={subCampusFilter}
                  label="Sub-Campus"
                  onChange={(e) => setSubCampusFilter(e.target.value)}
                >
                  {uniqueSubCampusesWithAll.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 150, ...inputSx }}>
                <InputLabel id="type-filter-label">Type</InputLabel>
                <Select
                  labelId="type-filter-label"
                  value={typeFilter}
                  label="Type"
                  onChange={(e) => setTypeFilter(e.target.value)}
                >
                  {uniqueTypesWithAll.map((t) => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 150, ...inputSx }}>
                <InputLabel id="status-filter-label">Status</InputLabel>
                <Select
                  labelId="status-filter-label"
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  {uniqueStatusesWithAll.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ flexGrow: 1 }} />

              <TextField
                sx={{ width: 260, ...inputSx }}
                size="small"
                placeholder="Search assets..."
                variant="outlined"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "rgba(25,118,210,0.75)" }} fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>

          <Paper elevation={0} sx={tableWrapSx}>
            <Box
              sx={{
                overflowX: "auto",
                overflowY: "hidden",
              }}
            >
              <TableContainer component="div">
                <Table size="small" stickyHeader={false}>
                  <TableHead>
                    <TableRow sx={theadRowSx}>
                      <TableCell sx={{ minWidth: 60 }}>S.NO</TableCell>
                      <TableCell sx={{ minWidth: 140 }}>Asset ID</TableCell>
                      <TableCell sx={{ minWidth: 120 }}>Sub-Campus</TableCell>
                      <TableCell sx={{ minWidth: 150 }}>Category</TableCell>
                      <TableCell sx={{ minWidth: 150 }}>Type</TableCell>
                      <TableCell sx={{ minWidth: 180 }}>Asset Name</TableCell>
                      <TableCell sx={{ minWidth: 140 }}>Serial Number</TableCell>
                      <TableCell sx={{ minWidth: 120 }}>Room Type</TableCell>
                      <TableCell sx={{ minWidth: 100 }}>Room Number</TableCell>
                      <TableCell sx={{ minWidth: 130 }}>Status</TableCell>
                      <TableCell sx={{ minWidth: 130 }}>Maintenance</TableCell>
                      <TableCell sx={{ minWidth: 130 }}>Repair</TableCell>
                      <TableCell sx={{ minWidth: 140 }}>Last Updated</TableCell>
                      <TableCell sx={{ minWidth: 100 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {filteredAssets.map((asset, index) => (
                      <TableRow key={index} sx={zebraRowSx(index)}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell sx={{ fontSize: "0.75rem", fontStyle: "italic", color: "text.secondary" }}>
                          {asset.AssetID || asset.assetid || asset.id || "-"}
                        </TableCell>

                        {editId === asset.id ? (
                          <>
                            <TableCell>
                              <Autocomplete
                                disableClearable
                                size="small"
                                options={uniqueSubCampuses}
                                value={asset.SubCampusCode || ""}
                                onChange={(e, newValue) => {
                                  handleInputChange(
                                    { target: { name: "SubCampusCode", value: newValue } },
                                    asset.id
                                  );
                                }}
                                renderInput={(params) => <TextField {...params} variant="outlined" sx={inputSx} />}
                              />
                            </TableCell>

                            <TableCell>
                              <Autocomplete
                                disableClearable
                                size="small"
                                options={isAdmin ? [...uniqueCategories, "Add New"] : uniqueCategories}
                                value={asset.AssetCategory || ""}
                                onChange={(event, newValue) => {
                                  if (newValue === "Add New") {
                                    setDialogTarget("AssetCategory");
                                    setEditingAssetId(asset.id);
                                    setDialogOpen(true);
                                  } else {
                                    handleInputChange(
                                      { target: { name: "AssetCategory", value: newValue } },
                                      asset.id
                                    );
                                  }
                                }}
                                renderInput={(params) => <TextField {...params} variant="outlined" sx={inputSx} />}
                                ListboxProps={{ sx: { "& .MuiAutocomplete-option": { whiteSpace: "nowrap" } } }}
                                slotProps={{ popper: { sx: { minWidth: "200px" } } }}
                              />
                            </TableCell>

                            <TableCell>
                              <Autocomplete
                                disableClearable
                                size="small"
                                options={isAdmin ? [...uniqueTypesList, "Add New"] : uniqueTypesList}
                                value={asset.AssetType || ""}
                                onChange={(event, newValue) => {
                                  if (newValue === "Add New") {
                                    setDialogTarget("AssetType");
                                    setEditingAssetId(asset.id);
                                    setDialogOpen(true);
                                  } else {
                                    handleInputChange(
                                      { target: { name: "AssetType", value: newValue } },
                                      asset.id
                                    );
                                  }
                                }}
                                renderInput={(params) => <TextField {...params} variant="outlined" sx={inputSx} />}
                                ListboxProps={{ sx: { "& .MuiAutocomplete-option": { whiteSpace: "nowrap" } } }}
                                slotProps={{ popper: { sx: { minWidth: "200px" } } }}
                              />
                            </TableCell>

                            <TableCell>
                              <TextField
                                size="small"
                                name="AssetName"
                                value={asset.AssetName}
                                onChange={(e) => handleInputChange(e, asset.id)}
                                sx={{ ...inputSx, width: 160 }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                name="SerialNumber"
                                value={asset.SerialNumber}
                                onChange={(e) => handleInputChange(e, asset.id)}
                                sx={{ ...inputSx, width: 120 }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                name="RoomType"
                                value={asset.RoomType}
                                onChange={(e) => handleInputChange(e, asset.id)}
                                sx={{ ...inputSx, width: 100 }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                name="RoomNumber"
                                value={asset.RoomNumber}
                                onChange={(e) => handleInputChange(e, asset.id)}
                                sx={{ ...inputSx, width: 80 }}
                              />
                            </TableCell>
                            <TableCell>
                              <Select
                                size="small"
                                name="Status"
                                value={asset.Status}
                                onChange={(e) => handleInputChange(e, asset.id)}
                                sx={{ minWidth: 110, ...inputSx }}
                              >
                                <MenuItem value="Active">Active</MenuItem>
                                <MenuItem value="Maintenance">Maintenance</MenuItem>
                                <MenuItem value="Damaged">Damaged</MenuItem>
                                <MenuItem value="Inactive">Inactive</MenuItem>
                              </Select>
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                name="Maintenance"
                                value={asset.Maintenance}
                                onChange={(e) => handleInputChange(e, asset.id)}
                                sx={{ ...inputSx, width: 110 }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                name="Repair"
                                value={asset.Repair}
                                onChange={(e) => handleInputChange(e, asset.id)}
                                sx={{ ...inputSx, width: 110 }}
                              />
                            </TableCell>
                            <TableCell>{formatDate(asset.LastUpdated)}</TableCell>
                            <TableCell>
                              <IconButton size="small" sx={iconBtnSx} onClick={() => handleUpdate(asset.id)} title="Save">
                                <SaveIcon fontSize="small" />
                              </IconButton>
                              <IconButton size="small" sx={iconBtnSx} onClick={() => setEditId(null)} title="Cancel">
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell>{asset.SubCampusCode}</TableCell>
                            <TableCell>{asset.AssetCategory}</TableCell>
                            <TableCell sx={{ fontSize: "0.75rem", fontStyle: "italic", color: "text.secondary" }}>
                              {asset.AssetType || asset.assettype}
                            </TableCell>
                            <TableCell>{asset.AssetName}</TableCell>
                            <TableCell>{asset.SerialNumber}</TableCell>
                            <TableCell>{asset.RoomType}</TableCell>
                            <TableCell>{asset.RoomNumber}</TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={badgeSx(asset.Status)}>
                                {asset.Status}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={badgeSx(asset.Maintenance)}>
                                {asset.Maintenance}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={badgeSx(asset.Repair)}>
                                {asset.Repair}
                              </Typography>
                            </TableCell>
                            <TableCell>{formatDate(asset.LastUpdated)}</TableCell>
                            <TableCell>
                              <IconButton size="small" sx={iconBtnSx} onClick={() => setEditId(asset.id)} title="Edit">
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton size="small" sx={iconBtnSx} onClick={() => handleDelete(asset.id)} title="Delete">
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    ))}

                    <TableRow sx={zebraRowSx(filteredAssets.length)}>
                      <TableCell sx={{ fontWeight: 700, color: "text.secondary" }}>
                        {filteredAssets.length + 1}
                      </TableCell>

                      <TableCell>
                        <TextField
                          size="small"
                          placeholder="ID"
                          name="AssetID"
                          value={newAsset.AssetID}
                          onChange={handleInputChange}
                          sx={{ ...inputSx, width: 100 }}
                        />
                      </TableCell>

                      <TableCell>
                        <Autocomplete
                          disableClearable
                          size="small"
                          options={uniqueSubCampuses}
                          value={newAsset.SubCampusCode || ""}
                          onChange={(e, newValue) => {
                            handleInputChange({ target: { name: "SubCampusCode", value: newValue } });
                          }}
                          renderInput={(params) => (
                            <TextField {...params} placeholder="Sub-Campus" variant="outlined" sx={inputSx} />
                          )}
                        />
                      </TableCell>

                      <TableCell>
                        <Autocomplete
                          disableClearable
                          size="small"
                          options={isAdmin ? [...uniqueCategories, "Add New"] : uniqueCategories}
                          value={newAsset.AssetCategory || ""}
                          onChange={(event, newValue) => {
                            if (newValue === "Add New") {
                              setDialogTarget("AssetCategory");
                              setEditingAssetId(null);
                              setDialogOpen(true);
                            } else {
                              handleInputChange({ target: { name: "AssetCategory", value: newValue } });
                            }
                          }}
                          renderInput={(params) => (
                            <TextField {...params} placeholder="Category" variant="outlined" sx={inputSx} />
                          )}
                          ListboxProps={{ sx: { "& .MuiAutocomplete-option": { whiteSpace: "nowrap" } } }}
                          slotProps={{ popper: { sx: { minWidth: "200px" } } }}
                        />
                      </TableCell>

                      <TableCell>
                        <Autocomplete
                          disableClearable
                          size="small"
                          options={isAdmin ? [...uniqueTypesList, "Add New"] : uniqueTypesList}
                          value={newAsset.AssetType || ""}
                          onChange={(event, newValue) => {
                            if (newValue === "Add New") {
                              setDialogTarget("AssetType");
                              setEditingAssetId(null);
                              setDialogOpen(true);
                            } else {
                              handleInputChange({ target: { name: "AssetType", value: newValue } });
                            }
                          }}
                          renderInput={(params) => (
                            <TextField {...params} placeholder="Type" variant="outlined" sx={inputSx} />
                          )}
                          ListboxProps={{ sx: { "& .MuiAutocomplete-option": { whiteSpace: "nowrap" } } }}
                          slotProps={{ popper: { sx: { minWidth: "200px" } } }}
                        />
                      </TableCell>

                      <TableCell>
                        <TextField
                          size="small"
                          placeholder="Name"
                          name="AssetName"
                          value={newAsset.AssetName}
                          onChange={(e) => handleInputChange(e)}
                          sx={{ ...inputSx, width: 160 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          placeholder="Serial"
                          name="SerialNumber"
                          value={newAsset.SerialNumber}
                          onChange={(e) => handleInputChange(e)}
                          sx={{ ...inputSx, width: 120 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          placeholder="RoomType"
                          name="RoomType"
                          value={newAsset.RoomType}
                          onChange={(e) => handleInputChange(e)}
                          sx={{ ...inputSx, width: 100 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          placeholder="Room No"
                          name="RoomNumber"
                          value={newAsset.RoomNumber}
                          onChange={(e) => handleInputChange(e)}
                          sx={{ ...inputSx, width: 80 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Select
                          size="small"
                          name="Status"
                          value={newAsset.Status}
                          onChange={(e) => handleInputChange(e)}
                          sx={{ minWidth: 110, ...inputSx }}
                        >
                          <MenuItem value="Active">Active</MenuItem>
                          <MenuItem value="Maintenance">Maintenance</MenuItem>
                          <MenuItem value="Damaged">Damaged</MenuItem>
                          <MenuItem value="Inactive">Inactive</MenuItem>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          placeholder="Maint"
                          name="Maintenance"
                          value={newAsset.Maintenance}
                          onChange={(e) => handleInputChange(e)}
                          sx={{ ...inputSx, width: 110 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          placeholder="Repair"
                          name="Repair"
                          value={newAsset.Repair}
                          onChange={(e) => handleInputChange(e)}
                          sx={{ ...inputSx, width: 110 }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.85rem", color: "text.secondary" }}>
                        {new Date().toISOString().split("T")[0]}
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" sx={iconBtnSx} onClick={handleAdd} title="Add">
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Paper>
        </Box>
      </Paper>

      <Snackbar open={success} autoHideDuration={3000} onClose={() => setSuccess(false)}>
        <Alert severity="success" sx={{ borderRadius: 2 }}>
          Operation successful!
        </Alert>
      </Snackbar>

      <Box component="div">
        <Alert
          severity="info"
          sx={{
            mt: 2,
            borderRadius: 2,
            border: `1px solid ${BORDER}`,
            backgroundColor: "rgba(25,118,210,0.04)",
          }}
        >
          <Typography variant="caption" fontWeight={700}>
            {isAdmin
              ? "Admin Mode: You can add new categories and types using the 'Add New' option."
              : "Viewer Mode: Only Admins can add new categories/types."}
          </Typography>
        </Alert>
      </Box>

      <Box component="div">
        {dialogOpen && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              bgcolor: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10000,
            }}
          >
            <Paper
              sx={{
                p: 3,
                width: 400,
                borderRadius: 2,
                boxShadow: 24,
                border: `1px solid ${BORDER}`,
              }}
            >
              <Typography variant="h6" fontWeight={900} gutterBottom>
                Add New{" "}
                {dialogTarget === "AssetCategory"
                  ? "Category"
                  : dialogTarget === "AssetType"
                  ? "Type"
                  : "Sub-Campus"}
              </Typography>

              <TextField
                fullWidth
                label="Name"
                variant="outlined"
                value={dialogValue}
                autoFocus
                onChange={(e) => setDialogValue(e.target.value)}
                sx={{ mt: 2, ...inputSx }}
              />

              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 3 }}>
                <Button
                  variant="outlined"
                  sx={outlinedBtnSx}
                  onClick={() => {
                    setDialogOpen(false);
                    setDialogValue("");
                    setEditingAssetId(null);
                  }}
                >
                  Cancel
                </Button>

                <Button
                  variant="contained"
                  sx={primaryBtnSx}
                  onClick={() => {
                    if (dialogValue.trim()) {
                      const newVal = dialogValue.trim();
                      handleInputChange(
                        { target: { name: dialogTarget, value: newVal } },
                        editingAssetId
                      );

                      if (dialogTarget === "AssetCategory") {
                        setManualCategories((prev) => [...new Set([...prev, newVal])]);
                      }
                      if (dialogTarget === "AssetType") {
                        setManualTypes((prev) => [...new Set([...prev, newVal])]);
                      }
                      if (dialogTarget === "SubCampusCode") {
                        setManualSubCampuses((prev) => [...new Set([...prev, newVal])]);
                      }

                      setDialogOpen(false);
                      setDialogValue("");
                      setEditingAssetId(null);
                    }
                  }}
                >
                  Save
                </Button>
              </Box>
            </Paper>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AssetManagement;